<script>
import Popin from "@/components/molecules/popin";
import Button from "@/components/atoms/button";
import Titre from "@/components/atoms/titre";
import LazyImage from "@/components/atoms/lazy-image";
import Container from "@/components/atoms/container";
import Table from "@/components/molecules/table";
import LoadingSpinner from "@/components/molecules/loading-spinner";
import PaymentMethodMultiPayError from "@/components/organisms/payment-method/payment-method-multi-pay/error";
import {
  popinMultiPayEmits,
  popinMultiPayProps,
  usePopinMultiPay
} from "./mixin";

import codeLivreur from "@/assets/images/multi-pay/code-livreur.png";
import borne from "@/assets/images/multi-pay/borne.png";
import signer from "@/assets/images/multi-pay/signer.png";
import cni from "@/assets/images/multi-pay/cni.png";
import sansFraisBlack4x from "@/assets/images/multi-pay/4x-sans-frais-black.png";
import sansFraisBlack3x from "@/assets/images/multi-pay/3x-sans-frais-black.png";

export default {
  name: "PopinMultiPay",
  components: {
    Popin,
    Button,
    Titre,
    LazyImage,
    Container,
    Table,
    LoadingSpinner,
    PaymentMethodMultiPayError
  },
  props: {
    ...popinMultiPayProps()
  },
  emits: [...popinMultiPayEmits()],
  setup(props, context) {
    const popin = ref(null);
    const { currentCode, loading, mensualites, show, close, updateEchancier } =
      usePopinMultiPay(props, context, popin);
    return {
      currentCode,
      loading,
      mensualites,
      show,
      close,
      updateEchancier,
      codeLivreur,
      borne,
      signer,
      cni,
      sansFraisBlack4x,
      sansFraisBlack3x,
      popin
    };
  }
};
</script>

<template>
  <Popin
    ref="popin"
    v-bind="removeClassAndStyleFromAttrs($attrs)"
    size="lg"
    class="c-popin-multi-pay"
  >
    <Container size="xs">
      <div class="u-text-center u-font-16 u-mb-lg25">
        <Titre
          tag="h1"
          variant="primary"
          class="u-mb-lg"
        >
          <template v-if="currentCode === '4FCB'">
            Payez en plusieurs fois avec votre carte bancaire
          </template>
          <template v-else>
            Payez en 3 fois sans frais avec votre carte bancaire
          </template>
        </Titre>
        <div class="c-popin-multi-pay__button-switch u-mb-lg">
          <template v-if="hasTroisFois">
            <input
              id="3FCB"
              v-model="currentCode"
              type="radio"
              class="c-popin-multi-pay__button-switch-input"
              value="3FCB"
              name="code"
              @change="updateEchancier('3FCB')"
            />
            <label
              for="3FCB"
              class="c-popin-multi-pay__button-switch-label"
            >
              Paiement en 3 fois sans frais
            </label>
          </template>
          <span
            v-if="hasTroisFois && hasQuatreFois"
            class="u-font-15 u-mx-sm"
          >
            ou
          </span>
          <template v-if="hasQuatreFois">
            <input
              id="4FCB"
              v-model="currentCode"
              type="radio"
              class="c-popin-multi-pay__button-switch-input"
              value="4FCB"
              name="code"
              @change="updateEchancier('4FCB')"
            />
            <label
              for="4FCB"
              class="c-popin-multi-pay__button-switch-label"
            >
              Paiement en 4 fois avec frais
            </label>
          </template>
        </div>
        <Table
          v-if="!loading && echeancier"
          size="full"
          no-bordered
          class="c-popin-multi-pay__table"
        >
          <thead>
            <tr>
              <th
                align="left"
                scope="col"
              >
                <span class="u-font-500">Échéancier</span>
              </th>
              <th
                align="left"
                scope="col"
                class="u-font-500"
              >
                <span class="u-font-500">Montant à payer</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <template
              v-for="(mensualite, index) in mensualites"
              :key="mensualite.date"
            >
              <tr>
                <th
                  scope="row"
                  align="left"
                >
                  {{ index === 0 ? "1er" : `${index + 1}ème` }} prélèvement le
                  {{ mensualite.date }}
                </th>
                <td align="left">
                  <span class="u-font-700">
                    {{ formatPrix(mensualite.montant) }}&nbsp;€
                  </span>
                  <template v-if="index === 0 && echeancier.frais">
                    <br />
                    <span class="u-font-12">
                      dont {{ formatPrix(echeancier.frais) }} € de frais de dossier
                    </span>
                  </template>
                </td>
              </tr>
            </template>
          </tbody>
        </Table>
        <PaymentMethodMultiPayError
          v-else-if="error"
          :error="error"
          class="u-mb-sm"
        />
        <LoadingSpinner
          v-else
          size="lg"
          style="justify-self: center;"
        />
        <Titre
          tag="h1"
          variant="primary"
          class="u-mb-xs25"
        >
          C'est simple, rapide et sécurisé
        </Titre>

        <div>
          <div class="u-flex u-flex-align-center u-mb-md">
            <LazyImage
              :src="
                currentCode === '3FCB'
                  ? sansFraisBlack3x
                  : sansFraisBlack4x
              "
              height="72"
              width="72"
              class="u-flex-self-start u-flex-shrink-none u-mr-xs25"
            />
            <div class="u-text-left u-font-secondary">
              <p class="u-font-18 u-font-700">
                Au moment du paiement, choisissez le paiement en
                {{ currentCode === "3FCB" ? "3 fois sans frais" : "4 fois" }}
              </p>
            </div>
          </div>
          <div class="u-flex u-flex-align-center u-mb-md">
            <LazyImage
              :src="cni"
              height="72"
              width="72"
              class="u-flex-self-start u-flex-shrink-none u-mr-xs25"
            />
            <div class="u-text-left u-font-secondary">
              <p class="u-font-18 u-font-700">
                Munissez-vous de votre carte bancaire et de votre smartphone
              </p>
              <p class="u-font-15">
                Les cartes étrangères, les cartes exclusivement de retrait et
                les cartes à autorisation systématique (Electron, Mastro,
                Cirrus...) ne sont pas acceptées, ni les e-cards.
              </p>
            </div>
          </div>
          <div class="u-flex u-flex-align-center u-mb-md">
            <LazyImage
              :src="signer"
              height="72"
              width="72"
              class="u-flex-self-start u-flex-shrink-none u-mr-xs25"
            />
            <div class="u-text-left u-font-secondary">
              <p class="u-font-18 u-font-700">
                Complétez les informations demandées par Cetelem
              </p>
            </div>
          </div>
          <div class="u-flex u-flex-align-center u-mb-md">
            <LazyImage
              :src="borne"
              height="72"
              width="72"
              class="u-flex-self-start u-flex-shrink-none u-mr-xs25"
            />
            <div class="u-text-left u-font-secondary">
              <p class="u-font-18 u-font-700">
                Paiement sécurisé 3DSecure : vous recevrez un code à 4 chiffres
                par SMS
              </p>
            </div>
          </div>
          <div class="u-flex u-flex-align-center">
            <LazyImage
              :src="codeLivreur"
              height="72"
              width="72"
              class="u-flex-self-start u-flex-shrink-none u-mr-xs25"
            />
            <div class="u-text-left u-font-secondary">
              <p class="u-font-18 u-font-700">
                Vous obtiendrez une réponse immédiate de notre partenaire
                Cetelem
              </p>
            </div>
          </div>
        </div>
        <div class="u-mt-lg">
          <Button
            variant="secondary"
            radius="full"
            size="md"
            @click="close"
          >
            Ok, j’ai compris
          </Button>
        </div>
      </div>
      <div class="u-font-12 u-color-text-light u-mb-md">
        <p>
          Découvrez notre offre plus en détail : <br />
          Il s’agit d’une solution de paiement en 3 fois ou 4 fois par carte bancaire Visa ou Mastercard qui permet d'échelonner le paiement de la commande :<br />
          <br />
          3 débits suivant l'échéancier suivant :
        </p>
        <li>1ère échéance : le jour de votre commande : 1/3 du montant du panier </li>
        <li>2ème échéance : 30 jours après votre commande : 1/3 du montant du panier</li>
        <li>3ème échéance : 60 jours après votre commande : 1/3 du montant du panier.</li>
        <br />
        <p>
          En 4 débits suivant l’échéancier suivant :
        </p>
        <li>1ère échéance : le jour de votre commande : ¼ du montant du panier + 1,70% de frais de dossier.</li>
        <li>
          2ème échéance : 30 jours après votre commande : ¼ du montant du panier
        </li>
        <li>3ème échéance : 60 jours après votre commande : ¼ du montant du panier</li>
        <li>4ème échéance : 85 jours après votre commande : ¼ du montant du panier</li>
        <br />
        <p>
          <b>Exemple pour un panier à régler de 300€ en 3 fois carte bancaire gratuit :</b><br />
          1ère échéance de 100€ suivie de 2 échéances de 100€ chacune.<br />
          Coût total de l’opération à crédit 300€. Les frais de dossier sont pris en charge par Cora.fr. Conditions en vigueur au 01/04/2022.<br />
          <br />
          <b>Exemple pour un panier à régler de 400€ en 4 fois carte bancaire :</b><br />
          1ère mensualité de 106,80€ dont 6,80€ de frais de dossier suivi de 3 mensualités égales de 100€ chacune.<br />
          Coût total de l’opération à crédit 406,80€. Conditions en vigueur au 01/04/2022<br />
          <br />
          <b>Conditions d’éligibilité :</b><br />
        </p>
        <li>Le montant de votre panier doit être compris entre {{ min }}€ et {{ max }}€.</li>
        <li>Le paiement doit s'effectuer avec une CB Visa ou Mastercard (hors cartes Electron, Maestro, American Express, e-CB et CB pré payée)</li>
        <li>Votre carte bancaire doit être valide dans les 90 jours qui suivent votre 1ère date d’échéance.</li>
        <li>Cette offre est réservée aux particuliers (personnes physiques majeures) résidant en France ou dans un département ou territoire français.</li>
        <br />
        <p>
          Si vous remplissez toutes les conditions d'éligibilité, le « paiement en 3 fois ou 4 fois par carte bancaire » vous sera proposé à l'étape du choix de paiement. <br />
          La souscription au 4xCB nécessite la signature électronique par double clic d’un contrat de financement de moins de 90 jours, contrat pour lequel vous disposez d’un délai de rétractation de 14 jours calendaires à compter de sa conclusion.<br />
          Aucun versement de quelque nature que ce soit ne peut être exigé d’un particulier avant l’obtention d’un ou plusieurs prêts d’argent.<br />
          Cette solution est proposée par le partenaire financier de Cora.fr : BNP Paribas Personal Finance, SA au capital de 546 601 552€ - SIREN 542 097 902 RCS Paris 1, boulevard Haussmann 75 318 Paris Cedex 09 - N°ORIAS : 07 023 128 Pour toutes vos questions sur le fonctionnement du « paiement en 3 fois ou 4 fois par carte bancaire », contactez-nous par mail à l’adresse <a href="mailto:3ou4xCB.cetelem@wcc-distribution.fr">3ou4xCB.cetelem@wcc-distribution.fr</a>
        </p>
      </div>
    </Container>
  </Popin>
</template>

<style lang="scss" scoped>
@import "@/assets/styles/components/popin-multi-pay.scss";
@import "@/assets/styles/components/table.scss";
</style>
